/**
 * Trainer workout detail — edit exercise (pixel-aligned to `exercise-edit.html`).
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, Info, Play, Plus, RefreshCw, X } from '../../bravosIcons';
import { isYouTubeUrl } from '../../../../utils/youtubeUtils';
import { getExerciseVideoThumbUrl, looksLikeDirectVideoUrl } from '../../../../utils/exerciseVideoThumb';
import { getExecutionStepsForDisplay, splitTags, stepsToDescription } from '../../../../utils/exerciseDisplayUtils';
import { reorderStepRows } from '../../../../utils/exerciseStepReorder';
import { useFocusTrap } from '../../../../hooks/useFocusTrap';
import type { CreateExerciseData, Exercise } from '../../../../types/dataModel';
import { StepDragDots } from '../TrainerWorkoutDetailEditIcons';
import './ExerciseEditDrawer.css';

const MAX_CHIPS = 10;

function newRowId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `r-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

type StepRow = { id: string; text: string };

function packDirtyKey(parts: {
  name: string;
  video: string;
  muscle: string[];
  equipment: string[];
  stepTexts: string[];
}): string {
  return JSON.stringify({
    name: parts.name.trim(),
    video: parts.video.trim(),
    muscle: [...parts.muscle].sort(),
    equipment: [...parts.equipment].sort(),
    stepTexts: parts.stepTexts.map((t) => t),
  });
}

function initialStepRows(exercise: Exercise): StepRow[] {
  const structured = getExecutionStepsForDisplay(exercise.description, exercise.steps);
  if (structured.length === 0) return [{ id: newRowId(), text: '' }];
  return structured.map((t) => ({ id: newRowId(), text: t }));
}

function autoResizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

export type ExerciseEditDrawerHandle = {
  /** Same as scrim / Cancel: respects dirty state and unsaved dialog. */
  requestClose: () => void;
};

export interface ExerciseEditDrawerProps {
  exercise: Exercise | null;
  open: boolean;
  muscleOptions: string[];
  equipmentOptions: string[];
  onSave: (data: CreateExerciseData) => Promise<Exercise>;
  onReturnToView: () => void;
  onSaveComplete: (exercise: Exercise) => void;
  onDiscard: () => Promise<void>;
  /** When false, scrim is omitted (parent supplies one shared layer). @default true */
  renderScrim?: boolean;
}

export const ExerciseEditDrawer = forwardRef<ExerciseEditDrawerHandle, ExerciseEditDrawerProps>(
  function ExerciseEditDrawer(
    {
      exercise,
      open,
      muscleOptions,
      equipmentOptions,
      onSave,
      onReturnToView,
      onSaveComplete,
      onDiscard,
      renderScrim = true,
    },
    ref,
  ) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const unsavedDialogRef = useRef<HTMLDivElement>(null);
  const stepsEditorRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const videoUrlInputRef = useRef<HTMLInputElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const dragSourceIndex = useRef<number | null>(null);

  const [name, setName] = useState('');
  const [video, setVideo] = useState('');
  const [muscle, setMuscle] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [stepRows, setStepRows] = useState<StepRow[]>([]);
  const [snapshotKey, setSnapshotKey] = useState('');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [openPicker, setOpenPicker] = useState<'muscle' | 'equipment' | null>(null);
  const [pickerFilter, setPickerFilter] = useState('');
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const musclePickerRef = useRef<HTMLDivElement>(null);
  const equipmentPickerRef = useRef<HTMLDivElement>(null);

  const resetFromExercise = useCallback((ex: Exercise) => {
    setName(ex.name ?? '');
    setVideo(ex.video?.trim() ? String(ex.video) : '');
    setMuscle(splitTags(ex.muscleGroup).slice(0, MAX_CHIPS));
    setEquipment(splitTags(ex.equipment).slice(0, MAX_CHIPS));
    setStepRows(initialStepRows(ex));
    setNameError('');
    setShowUnsaved(false);
    setOpenPicker(null);
    setPickerFilter('');
    setDraggingIndex(null);
    setDragOverIndex(null);
    dragSourceIndex.current = null;
    const sk = packDirtyKey({
      name: ex.name ?? '',
      video: ex.video?.trim() ? String(ex.video) : '',
      muscle: splitTags(ex.muscleGroup).slice(0, MAX_CHIPS),
      equipment: splitTags(ex.equipment).slice(0, MAX_CHIPS),
      stepTexts: initialStepRows(ex).map((r) => r.text),
    });
    setSnapshotKey(sk);
  }, []);

  useEffect(() => {
    if (!open || !exercise) return;
    resetFromExercise(exercise);
    requestAnimationFrame(() => closeBtnRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exercise?.id]);

  useLayoutEffect(() => {
    if (!open) return;
    const root = stepsEditorRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLTextAreaElement>('.eed-step-input').forEach(autoResizeTextarea);
  }, [stepRows, open]);

  const dirtyKey = useMemo(
    () =>
      packDirtyKey({
        name,
        video,
        muscle,
        equipment,
        stepTexts: stepRows.map((r) => r.text),
      }),
    [name, video, muscle, equipment, stepRows],
  );

  const dirty = open && exercise && dirtyKey !== snapshotKey;

  const tryClose = useCallback(() => {
    if (!exercise) return;
    if (dirty) {
      setShowUnsaved(true);
      return;
    }
    onReturnToView();
  }, [dirty, exercise, onReturnToView]);

  useImperativeHandle(
    ref,
    () => ({
      requestClose: () => {
        tryClose();
      },
    }),
    [tryClose],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showUnsaved) {
        e.preventDefault();
        keepEditingRef.current?.focus();
        return;
      }
      e.preventDefault();
      tryClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, showUnsaved, tryClose]);

  useFocusTrap(drawerRef, open && !showUnsaved);
  useFocusTrap(unsavedDialogRef, open && showUnsaved);

  useEffect(() => {
    if (!showUnsaved) return;
    requestAnimationFrame(() => keepEditingRef.current?.focus());
  }, [showUnsaved]);

  useEffect(() => {
    if (!openPicker) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (openPicker === 'muscle' && musclePickerRef.current?.contains(t)) return;
      if (openPicker === 'equipment' && equipmentPickerRef.current?.contains(t)) return;
      setOpenPicker(null);
      setPickerFilter('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openPicker]);

  const videoTrim = video.trim();
  const thumbSrc = useMemo(
    () => (exercise && videoTrim ? getExerciseVideoThumbUrl(videoTrim, exercise.image) : null),
    [videoTrim, exercise],
  );
  const showNativeVideoThumb = Boolean(
    videoTrim && !isYouTubeUrl(videoTrim) && looksLikeDirectVideoUrl(videoTrim) && !thumbSrc,
  );

  const addChip = (kind: 'muscle' | 'equipment', value: string) => {
    const v = value.trim();
    if (!v) return;
    if (kind === 'muscle') {
      if (muscle.length >= MAX_CHIPS) return;
      if (muscle.some((x) => x.toLowerCase() === v.toLowerCase())) return;
      setMuscle((m) => [...m, v]);
    } else {
      if (equipment.length >= MAX_CHIPS) return;
      if (equipment.some((x) => x.toLowerCase() === v.toLowerCase())) return;
      setEquipment((m) => [...m, v]);
    }
    setOpenPicker(null);
    setPickerFilter('');
  };

  const filteredMuscle = useMemo(() => {
    const f = pickerFilter.trim().toLowerCase();
    return muscleOptions.filter(
      (o) =>
        !muscle.some((m) => m.toLowerCase() === o.toLowerCase()) &&
        (!f || o.toLowerCase().includes(f)),
    );
  }, [muscleOptions, muscle, pickerFilter]);

  const filteredEquipment = useMemo(() => {
    const f = pickerFilter.trim().toLowerCase();
    return equipmentOptions.filter(
      (o) =>
        !equipment.some((m) => m.toLowerCase() === o.toLowerCase()) &&
        (!f || o.toLowerCase().includes(f)),
    );
  }, [equipmentOptions, equipment, pickerFilter]);

  const handleSave = async () => {
    if (!exercise) return;
    const nt = name.trim();
    if (!nt) {
      setNameError('Exercise name is required');
      nameInputRef.current?.focus();
      return;
    }
    setNameError('');
    const steps = stepRows.map((r) => r.text.trim()).filter(Boolean);
    const description = stepsToDescription(steps);
    const payload: CreateExerciseData = {
      name: nt,
      muscleGroup: muscle.length ? muscle.join(', ') : undefined,
      equipment: equipment.length ? equipment.join(', ') : undefined,
      video: videoTrim || undefined,
      description,
    };
    setSaving(true);
    try {
      const merged = await onSave(payload);
      onSaveComplete(merged);
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error ? err.message : 'Failed to save. Please try again.';
      window.alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    setShowUnsaved(false);
    await onDiscard();
  };

  const focusVideoField = () => {
    videoUrlInputRef.current?.focus();
    videoUrlInputRef.current?.select();
  };

  const exerciseTitleId = exercise ? `eed-title-${exercise.id}` : 'eed-title';

  /**
   * True for rows that aren't owned by the current trainer (i.e. Bravos / shared
   * library). Drives the "Edits are personal" override notice — owned rows save
   * to the canonical exercise, so the notice would be misleading there.
   */
  const isLibraryExercise = !exercise || exercise.createdBy == null || String(exercise.createdBy).trim() === '';

  if (!open || !exercise) return null;

  const ui = (
    <>
      {renderScrim ? <div className="eed-scrim" aria-hidden="true" onClick={() => tryClose()} /> : null}

      <aside
        className={`eed-drawer${showUnsaved ? ' eed-drawer--tilt' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={exerciseTitleId}
      >
        <div
          ref={drawerRef}
          className={`eed-drawer-main${showUnsaved ? ' eed-drawer-main--blocked' : ''}`}
          aria-hidden={showUnsaved ? true : undefined}
        >
          <header className="eed-header">
            <div className="eed-eyebrow">
              <span className="eed-eyebrow-dot" aria-hidden="true" />
              Edit exercise
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              className="eed-close-btn"
              aria-label="Close"
              onClick={() => tryClose()}
            >
              <X width={14} height={14} strokeWidth={2.2} aria-hidden="true" />
            </button>
          </header>

          <div className="eed-body">
            {/*
             * Only Bravos / shared-library exercises get the override notice — saving an
             * exercise the trainer owns updates the canonical row, so the "personal copy"
             * framing would be misleading.
             */}
            {isLibraryExercise ? (
              <div className="eed-visibility-note">
                <span className="eed-visibility-note-icon" aria-hidden="true">
                  <Info width={11} height={11} strokeWidth={2.5} />
                </span>
                <span className="eed-visibility-note-text">
                  <strong>Edits are personal.</strong> Your changes are saved only to your version of this exercise.
                  Other trainers will continue to see the original.
                </span>
              </div>
            ) : null}

            <div className="eed-field-group">
              <div className="eed-field-label">
                Exercise name <span className="eed-req">*</span>
              </div>
              <input
                ref={nameInputRef}
                id={exerciseTitleId}
                type="text"
                className="eed-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
              {nameError ? <p className="eed-field-error">{nameError}</p> : null}
            </div>

            <div className="eed-field-group">
              <div className="eed-field-label">Video</div>
              <div className="eed-video-section">
                <div className="eed-video-thumb">
                  {thumbSrc ? (
                    <>
                      <img className="eed-video-thumb-media" src={thumbSrc} alt="" loading="lazy" />
                      <div className="eed-video-thumb-play" aria-hidden="true">
                        <Play width={14} height={14} fill="currentColor" strokeWidth={0} />
                      </div>
                    </>
                  ) : showNativeVideoThumb ? (
                    <>
                      <video
                        className="eed-video-thumb-media"
                        src={videoTrim}
                        muted
                        playsInline
                        preload="metadata"
                        aria-hidden="true"
                      />
                      <div className="eed-video-thumb-play" aria-hidden="true">
                        <Play width={14} height={14} fill="currentColor" strokeWidth={0} />
                      </div>
                    </>
                  ) : (
                    <div className="eed-video-thumb-fallback" aria-hidden="true" />
                  )}
                  <button type="button" className="eed-video-thumb-hit" aria-label="Replace video URL" onClick={focusVideoField}>
                    <span className="eed-video-thumb-overlay">
                      <RefreshCw width={18} height={18} strokeWidth={2} />
                      Replace
                    </span>
                  </button>
                </div>
                <div className="eed-video-fields">
                  <input
                    ref={videoUrlInputRef}
                    id={`eed-video-${exercise.id}`}
                    type="url"
                    className="eed-video-url-input"
                    value={video}
                    onChange={(e) => setVideo(e.target.value)}
                    placeholder="Paste video URL"
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            <div className="eed-field-group">
              <div className="eed-field-label">Muscle group</div>
              <div className="eed-tag-editor">
                {muscle.map((t) => (
                  <span key={t} className="eed-tag-edit eed-tag-edit-muscle">
                    {t}
                    <button
                      type="button"
                      className="eed-tag-edit-remove"
                      aria-label={`Remove ${t}`}
                      onClick={() => setMuscle((m) => m.filter((x) => x !== t))}
                    >
                      <X width={9} height={9} strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
                <div className="eed-chip-add-wrap" ref={musclePickerRef}>
                  <button
                    type="button"
                    className="eed-tag-add"
                    disabled={muscle.length >= MAX_CHIPS}
                    onClick={() => {
                      setOpenPicker((p) => (p === 'muscle' ? null : 'muscle'));
                      setPickerFilter('');
                    }}
                  >
                    <Plus width={11} height={11} strokeWidth={2.5} />
                    Add
                  </button>
                  {openPicker === 'muscle' && (
                    <div className="eed-dropdown">
                      <input
                        type="search"
                        placeholder="Filter…"
                        value={pickerFilter}
                        onChange={(e) => setPickerFilter(e.target.value)}
                        aria-label="Filter muscle groups"
                      />
                      {filteredMuscle.length === 0 ? (
                        <div className="eed-dropdown-empty">No matches</div>
                      ) : (
                        filteredMuscle.map((o) => (
                          <button key={o} type="button" onClick={() => addChip('muscle', o)}>
                            {o}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="eed-field-group">
              <div className="eed-field-label">Equipment</div>
              <div className="eed-tag-editor">
                {equipment.map((t) => (
                  <span key={t} className="eed-tag-edit">
                    {t}
                    <button
                      type="button"
                      className="eed-tag-edit-remove"
                      aria-label={`Remove ${t}`}
                      onClick={() => setEquipment((m) => m.filter((x) => x !== t))}
                    >
                      <X width={9} height={9} strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
                <div className="eed-chip-add-wrap" ref={equipmentPickerRef}>
                  <button
                    type="button"
                    className="eed-tag-add"
                    disabled={equipment.length >= MAX_CHIPS}
                    onClick={() => {
                      setOpenPicker((p) => (p === 'equipment' ? null : 'equipment'));
                      setPickerFilter('');
                    }}
                  >
                    <Plus width={11} height={11} strokeWidth={2.5} />
                    Add
                  </button>
                  {openPicker === 'equipment' && (
                    <div className="eed-dropdown">
                      <input
                        type="search"
                        placeholder="Filter…"
                        value={pickerFilter}
                        onChange={(e) => setPickerFilter(e.target.value)}
                        aria-label="Filter equipment"
                      />
                      {filteredEquipment.length === 0 ? (
                        <div className="eed-dropdown-empty">No matches</div>
                      ) : (
                        filteredEquipment.map((o) => (
                          <button key={o} type="button" onClick={() => addChip('equipment', o)}>
                            {o}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="eed-field-group">
              <div className="eed-field-label">Execution</div>
              <div ref={stepsEditorRef} className="eed-steps-editor">
                {stepRows.map((row, i) => (
                  <div
                    key={row.id}
                    className={`eed-step-row${draggingIndex === i ? ' eed-step-row-dragging' : ''}${dragOverIndex === i ? ' eed-step-row-drag-over' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragSourceIndex.current == null) return;
                      setDragOverIndex(i);
                    }}
                    onDragLeave={(e) => {
                      const rel = e.relatedTarget as Node | null;
                      if (rel && (e.currentTarget as HTMLElement).contains(rel)) return;
                      setDragOverIndex((cur) => (cur === i ? null : cur));
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragSourceIndex.current;
                      dragSourceIndex.current = null;
                      setDraggingIndex(null);
                      setDragOverIndex(null);
                      if (from == null) return;
                      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      const insertBefore = e.clientY < rect.top + rect.height / 2;
                      setStepRows((prev) => reorderStepRows(prev, from, i, insertBefore));
                    }}
                  >
                    <span
                      className="eed-step-drag"
                      draggable
                      role="button"
                      tabIndex={-1}
                      aria-label="Drag to reorder"
                      onDragStart={(e) => {
                        dragSourceIndex.current = i;
                        setDraggingIndex(i);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', '');
                        try {
                          const rowEl = (e.currentTarget as HTMLElement).closest('.eed-step-row');
                          if (rowEl && e.dataTransfer.setDragImage) {
                            e.dataTransfer.setDragImage(rowEl as Element, 20, 20);
                          }
                        } catch {
                          // ignore
                        }
                      }}
                      onDragEnd={() => {
                        dragSourceIndex.current = null;
                        setDraggingIndex(null);
                        setDragOverIndex(null);
                      }}
                    >
                      <StepDragDots />
                    </span>
                    <span className="eed-step-num tnum">{String(i + 1).padStart(2, '0')}</span>
                    <textarea
                      className="eed-step-input"
                      rows={1}
                      value={row.text}
                      placeholder="Describe this step…"
                      onChange={(e) => {
                        const v = e.target.value;
                        setStepRows((rows) => rows.map((r) => (r.id === row.id ? { ...r, text: v } : r)));
                        requestAnimationFrame(() => autoResizeTextarea(e.target));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          const newId = newRowId();
                          setStepRows((rows) => {
                            const idx = rows.findIndex((r) => r.id === row.id);
                            const next = [...rows];
                            next.splice(idx + 1, 0, { id: newId, text: '' });
                            return next;
                          });
                          requestAnimationFrame(() => {
                            const root = stepsEditorRef.current;
                            const tas = root?.querySelectorAll<HTMLTextAreaElement>('.eed-step-input');
                            const list = tas ? Array.from(tas) : [];
                            const newTa = list[i + 1];
                            newTa?.focus();
                          });
                          return;
                        }
                        if (e.key === 'Backspace' && row.text === '' && stepRows.length > 1) {
                          e.preventDefault();
                          setStepRows((rows) => rows.filter((r) => r.id !== row.id));
                          requestAnimationFrame(() => {
                            const root = stepsEditorRef.current;
                            const list = root?.querySelectorAll<HTMLTextAreaElement>('.eed-step-input');
                            const ta = list?.[i - 1];
                            ta?.focus();
                            if (ta) {
                              const len = ta.value.length;
                              ta.setSelectionRange(len, len);
                            }
                          });
                        }
                      }}
                      aria-label={`Step ${i + 1}`}
                    />
                    <button
                      type="button"
                      className="eed-step-remove"
                      aria-label="Remove step"
                      onClick={() => {
                        if (stepRows.length === 1) {
                          setStepRows([{ id: row.id, text: '' }]);
                          return;
                        }
                        setStepRows((rows) => rows.filter((r) => r.id !== row.id));
                      }}
                    >
                      <X width={11} height={11} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="eed-add-step-btn"
                onClick={() => {
                  const newId = newRowId();
                  setStepRows((r) => [...r, { id: newId, text: '' }]);
                  requestAnimationFrame(() => {
                    const root = stepsEditorRef.current;
                    const list = root?.querySelectorAll<HTMLTextAreaElement>('.eed-step-input');
                    list?.[list.length - 1]?.focus();
                  });
                }}
              >
                <Plus width={11} height={11} strokeWidth={2.5} />
                Add step
              </button>
            </div>
          </div>

          <footer className="eed-footer">
            <span className="eed-footer-status">
              {dirty ? (
                <>
                  <span className="eed-footer-pulse" aria-hidden="true" />
                  Unsaved changes
                </>
              ) : (
                '\u00a0'
              )}
            </span>
            <button type="button" className="eed-btn eed-btn-ghost" disabled={saving} onClick={() => tryClose()}>
              Cancel
            </button>
            <button type="button" className="eed-btn eed-btn-primary" disabled={saving} onClick={() => void handleSave()}>
              <Check width={14} height={14} strokeWidth={2.4} aria-hidden="true" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </footer>
        </div>

        {showUnsaved && (
          <div className="eed-unsaved-layer">
            <div className="eed-unsaved-backdrop" aria-hidden="true" />
            <div
              ref={unsavedDialogRef}
              className="eed-unsaved-dialog"
              role="alertdialog"
              aria-labelledby="eed-unsaved-title"
              aria-describedby="eed-unsaved-desc"
            >
              <button
                type="button"
                className="tr-dialog__close"
                aria-label="Close"
                onClick={() => setShowUnsaved(false)}
              >
                <X width={16} height={16} aria-hidden />
              </button>
              <h2 id="eed-unsaved-title" className="eed-unsaved-title">
                You have unsaved changes
              </h2>
              <p id="eed-unsaved-desc" className="eed-unsaved-body">
                If you close now, the edits you&apos;ve made to <strong>{exercise.name}</strong> will be lost.
                Save them to keep working with the updated version.
              </p>
              <div className="eed-unsaved-actions">
                <button
                  ref={keepEditingRef}
                  type="button"
                  className="eed-btn eed-btn-ghost"
                  onClick={() => setShowUnsaved(false)}
                >
                  Keep editing
                </button>
                <button type="button" className="eed-btn-danger" onClick={() => void handleDiscard()}>
                  Discard changes
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );

  return createPortal(ui, document.body);
});

ExerciseEditDrawer.displayName = 'ExerciseEditDrawer';
